import React from "react";
import { Helmet } from "react-helmet";
import {
  Headline,
  Paragraph,
  Section,
  ContentBlock,
  InlineLink,
} from "../components/ui";

const Datenschutz = () => {
  return (
    <>
      <Helmet>
        <title>Datenschutz | WTM Sportwagen</title>
        <meta
          name="description"
          content="Datenschutzerklärung der WTM Sportwagen Werkstatt in Gießen nach DSGVO."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Section variant="primary" spacing="spacious" margin={false}>
        <ContentBlock maxWidth="4xl" className="mx-auto">
          <Headline variant="h1" color="secondary" className="text-left mb-12">
            Datenschutz
          </Headline>

          {/* 1. Allgemeine Hinweise */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              1. Allgemeine Hinweise
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Wir informieren Sie nachfolgend gemäß den gesetzlichen Vorgaben
              der Datenschutzgrundverordnung (DS-GVO) über die Art, den Umfang
              und Zweck der Verarbeitung personenbezogener Daten im Rahmen
              unseres Onlineangebots.
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Diese Datenschutzerklärung gilt für unsere Website und unsere
              Social-Media-Profile.
            </Paragraph>
          </div>

          {/* 2. Verantwortlicher */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              2. Verantwortlicher
            </Headline>
            <Paragraph variant="medium" color="secondary">
              WTM Sportwagen Werkstatt
              <br />
              Inhaber: Andreas Wutschke
              <br />
              Grünberger Straße 140
              <br />
              35394 Gießen
              <br />
              E-Mail: werkstatt@wtm-sportwagen.com
            </Paragraph>
          </div>

          {/* 3. Webhosting */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              3. Webhosting
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Unsere Website wird bei folgendem Anbieter gehostet:
            </Paragraph>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              DigitalOcean LLC
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Mit dem Hostinganbieter wurde ein Auftragsverarbeitungsvertrag
              gemäß Art. 28 DS-GVO abgeschlossen. Dabei werden Meta- und
              Kommunikationsdaten (z. B. IP-Adresse, Browserdaten) verarbeitet.
            </Paragraph>
          </div>

          {/* 4. Cookie-Consent-Tool */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              4. Einsatz eines Cookie-Consent-Tools
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Beim ersten Besuch unserer Website erscheint ein Cookie-Banner,
              das Sie über die Verwendung von Cookies informiert. Sie haben dort
              die Möglichkeit, Cookies zu akzeptieren oder abzulehnen. Diese
              Einstellungen können jederzeit über einen Link im Footer angepasst
              werden.
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Die Datenverarbeitung erfolgt auf Basis von Art. 6 Abs. 1 lit. a)
              DSGVO (Einwilligung) bzw. bei notwendigen Cookies auf Basis von
              Art. 6 Abs. 1 lit. f) DSGVO (berechtigtes Interesse).
            </Paragraph>
          </div>

          {/* 5. Kontaktformular */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              5. Kontaktformular und Kommunikation
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Wenn Sie uns per Kontaktformular oder E-Mail kontaktieren,
              speichern wir Ihre Angaben (Name, E-Mail-Adresse, ggf.
              Telefonnummer und Nachricht), um Ihre Anfrage zu bearbeiten.
            </Paragraph>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b) DSGVO
              (vorvertragliche Maßnahme)
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Die Daten werden gelöscht, sobald der Zweck entfällt, es sei denn
              gesetzliche Aufbewahrungspflichten bestehen.
            </Paragraph>
          </div>

          {/* 6. WhatsApp Business */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              6. WhatsApp Business
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Unsere Website bietet einen Button zur Kontaktaufnahme via
              WhatsApp. Anbieter ist WhatsApp Ireland Limited.
            </Paragraph>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Die Nutzung ist freiwillig und erfolgt auf Grundlage Ihrer
              Einwilligung gemäß Art. 6 Abs. 1 lit. a) DSGVO.
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Bitte beachten Sie, dass bei der Nutzung Daten (z. B. Ihre
              Telefonnummer) in Drittländer übertragen werden können.
            </Paragraph>
          </div>

          {/* 7. Google Analytics */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              7. Google Analytics mit Consent Mode v2
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Wir verwenden Google Analytics der Google Ireland Limited. Ihre
              Daten werden nur verarbeitet, wenn Sie zuvor über unser
              Cookie-Consent-Banner zugestimmt haben.
            </Paragraph>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[var(--color-secondary)] opacity-90">
              <li>IP-Anonymisierung ist aktiviert</li>
              <li>Consent Mode v2 ist integriert</li>
              <li>
                Datenübermittlung erfolgt nur mit gültiger Einwilligung gemäß
                Art. 6 Abs. 1 lit. a) DSGVO
              </li>
            </ul>
            <Paragraph variant="medium" color="secondary">
              Weitere Infos:{" "}
              <InlineLink href="https://policies.google.com/privacy" external>
                https://policies.google.com/privacy
              </InlineLink>
            </Paragraph>
          </div>

          {/* 8. Google Maps */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              8. Google Maps
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Wir nutzen Google Maps zur Darstellung unseres Standorts.
            </Paragraph>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Die Einbindung erfolgt nur nach Ihrer aktiven Zustimmung im
              Consent-Banner (Art. 6 Abs. 1 lit. a) DSGVO).
            </Paragraph>
            <Paragraph variant="medium" color="secondary">
              Es kann eine Datenübertragung in die USA stattfinden.
            </Paragraph>
          </div>

          {/* 9. Server-Logfiles */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              9. Server-Logfiles
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Bei jedem Zugriff auf unsere Website werden folgende Daten
              automatisiert erfasst:
            </Paragraph>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[var(--color-secondary)] opacity-90">
              <li>IP-Adresse</li>
              <li>Datum/Uhrzeit</li>
              <li>Browsertyp und -version</li>
              <li>Referrer-URL</li>
              <li>Betriebssystem</li>
            </ul>
            <Paragraph variant="medium" color="secondary">
              Die Speicherung erfolgt aus Sicherheitsgründen für max. 70 Tage
              auf Basis von Art. 6 Abs. 1 lit. f) DSGVO.
            </Paragraph>
          </div>

          {/* 10. Cookies im Detail */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              10. Cookies im Detail
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-6">
              Unsere Website verwendet folgende Cookies:
            </Paragraph>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-[var(--color-secondary)] opacity-75">
                <thead>
                  <tr className="bg-[var(--color-primary-contrast)]">
                    <th className="border border-[var(--color-secondary)] opacity-75 p-3 text-left text-[var(--color-secondary)]">
                      Cookie-Typ
                    </th>
                    <th className="border border-[var(--color-secondary)] opacity-75 p-3 text-left text-[var(--color-secondary)]">
                      Beschreibung
                    </th>
                    <th className="border border-[var(--color-secondary)] opacity-75 p-3 text-left text-[var(--color-secondary)]">
                      Rechtsgrundlage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Essenzielle Cookies
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Für den technischen Betrieb erforderlich
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Art. 6 Abs. 1 lit. f) DSGVO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Analyse-Cookies
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Google Analytics (mit Einwilligung)
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Art. 6 Abs. 1 lit. a) DSGVO
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Drittanbieter
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      z. B. Google Maps, YouTube, Meta Pixel
                    </td>
                    <td className="border border-[var(--color-secondary)] opacity-75 p-3 text-[var(--color-secondary)]">
                      Art. 6 Abs. 1 lit. a) DSGVO
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Paragraph variant="medium" color="secondary">
              Sie können Cookies jederzeit über die Einstellungen im Browser
              löschen oder blockieren.
            </Paragraph>
          </div>

          {/* 11. Ihre Rechte */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              11. Ihre Rechte
            </Headline>
            <Paragraph variant="medium" color="secondary" className="mb-4">
              Sie haben jederzeit das Recht auf:
            </Paragraph>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[var(--color-secondary)] opacity-90">
              <li>Auskunft (Art. 15 DSGVO)</li>
              <li>Berichtigung (Art. 16 DSGVO)</li>
              <li>Löschung (Art. 17 DSGVO)</li>
              <li>Einschränkung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch (Art. 21 DSGVO)</li>
              <li>Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)</li>
              <li>Beschwerde bei der Datenschutzbehörde (Art. 77 DSGVO)</li>
            </ul>
          </div>

          {/* 12. Datensicherheit */}
          <div className="mb-12">
            <Headline variant="h3" color="secondary" className="text-left mb-6">
              12. Datensicherheit
            </Headline>
            <Paragraph variant="medium" color="secondary">
              Ihre Daten werden auf unserer Website per SSL verschlüsselt
              übertragen. Wir setzen geeignete technische und organisatorische
              Maßnahmen ein, um Ihre Daten gegen Verlust, Manipulation und
              unbefugten Zugriff zu schützen.
            </Paragraph>
          </div>
        </ContentBlock>
      </Section>
    </>
  );
};

export default Datenschutz;
